import { useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  activeViewIdAtomFamily,
  entityIdSF,
  Log,
  ViewApi,
  ViewDto,
  viewDtosAtomFamily,
  ViewScopeEnum,
} from "@ryde/shared";

import { dashboardCardsAtom } from "../dynamic-dashboard.atoms";
import { DASHBOARD_DEFAULT_CARDS } from "../dynamic-dashboard.default-layout";
import { DASHBOARD_ENTITY } from "../dynamic-dashboard.models";
import { IUseDashboardViews } from "./dashboard-views.models";
import {
  cloneCards,
  findDefaultView,
  readViewCards,
  toViewState,
} from "./dashboard-views.utils";

export type { IUseDashboardViews } from "./dashboard-views.models";

/**
 * Saved-views logic backed by the system's real `ViewApi` + view atoms
 * (`viewDtosAtomFamily` / `activeViewIdAtomFamily`), keyed by the `dashboard`
 * entity. The card layout is persisted inside each view's `viewState`
 * (`dashboardConfig.cards`). The base "Default" layout (today's dashboard,
 * `DASHBOARD_DEFAULT_CARDS`) is not stored as a view; `resetToDefault` restores it.
 */
export const useDashboardViews = ({
  autoLoad = false,
}: { autoLoad?: boolean } = {}): IUseDashboardViews => {
  const [cards, setCards] = useRecoilState(dashboardCardsAtom);
  const entityId = useRecoilValue(entityIdSF(DASHBOARD_ENTITY));
  const [views, setViews] = useRecoilState(
    viewDtosAtomFamily(DASHBOARD_ENTITY),
  );
  const [activeViewId, setActiveViewId] = useRecoilState(
    activeViewIdAtomFamily(DASHBOARD_ENTITY),
  );
  const [loading, setLoading] = useState(false);

  const viewList = useMemo(() => views ?? [], [views]);
  const defaultView = useMemo(() => findDefaultView(viewList), [viewList]);

  const reloadTokenRef = useRef(0);
  const reloadViews = useEvent(async (): Promise<void> => {
    if (!entityId) {
      return;
    }
    // Last-request-wins: a slower earlier reload must not overwrite a newer one.
    const token = ++reloadTokenRef.current;
    setLoading(true);
    try {
      const result = await ViewApi.getAllForEntity({ entityId });
      if (token === reloadTokenRef.current) {
        setViews(result ?? []);
      }
    } catch (err) {
      // Runs fire-and-forget in the mount effect — swallow so it can't become an
      // unhandled rejection (the API layer surfaces the error).
      //
      // Resolve to `[]`, not undefined: consumers read undefined as "still loading" and wait on it
      // (see `useIsDashboardLayoutResolved`), so a failure has to resolve too or the dashboard never
      // asks for its data. Cost: the autoLoad guard below then sees a value, so a failed load is not
      // retried on remount the way an unresolved one was.
      if (token === reloadTokenRef.current) {
        setViews([]);
      }

      Log.error("Failed to load dashboard views", err);
    } finally {
      if (token === reloadTokenRef.current) {
        setLoading(false);
      }
    }
  });

  // Load the dashboard's saved views once the entity id resolves. Only the
  // opted-in owner (the view panel, `autoLoad: true`) fetches — otherwise every
  // consumer of this hook (~4 mounted at once) would fire the same GET in
  // parallel on cold load.
  useEffect(() => {
    if (autoLoad && entityId && views === undefined) {
      reloadViews();
    }
  }, [autoLoad, entityId, views, reloadViews]);

  const generateName = (): string => `Dashboard view ${viewList.length + 1}`;

  const applyView = useEvent((id: number): void => {
    const viewCards = readViewCards(viewList.find(view => view.id === id));
    if (viewCards) {
      setCards(cloneCards(viewCards));
    }
    setActiveViewId(id);
  });

  const clearActiveView = useEvent((): void => {
    setActiveViewId(undefined);
  });

  const saveNewView = useEvent(
    async (
      name: string,
      scope: ViewScopeEnum,
      isDefault: boolean,
    ): Promise<void> => {
      if (!entityId) {
        return;
      }
      const created = await ViewApi.create({
        name,
        isDefault,
        scope,
        entityId,
        viewState: toViewState(cards),
      });
      await reloadViews();
      if (created?.id) {
        setActiveViewId(created.id);
      }
    },
  );

  const updateView = useEvent(async (id: number): Promise<void> => {
    if (!entityId) {
      return;
    }
    try {
      await ViewApi.update(id, {
        entityId,
        viewState: toViewState(cards) as ViewDto["viewState"],
      });
      await reloadViews();
      setActiveViewId(id);
    } catch (err) {
      Log.error("Failed to update dashboard view", err);
    }
  });

  const deleteView = useEvent(async (id: number): Promise<void> => {
    try {
      await ViewApi.remove(id);
      if (activeViewId === id) {
        setActiveViewId(undefined);
      }
      await reloadViews();
    } catch (err) {
      Log.error("Failed to delete dashboard view", err);
    }
  });

  // Apply a view returned by the API straight into the atom (no extra GET round
  // trip → faster, and the change shows immediately). Only one view can be
  // default, so clear the flag on the others when this one becomes default.
  const applyUpdatedView = useEvent((updated: ViewDto): void => {
    setViews(prev =>
      (prev ?? []).map(view => {
        if (view.id === updated.id) {
          return updated;
        }
        return updated.isDefault ? { ...view, isDefault: false } : view;
      }),
    );
  });

  const setDefaultView = useEvent(async (id: number): Promise<void> => {
    if (!entityId) {
      return;
    }
    try {
      const updated = await ViewApi.update(id, { entityId, isDefault: true });
      if (updated) {
        applyUpdatedView(updated);
      } else {
        await reloadViews();
      }
    } catch (err) {
      Log.error("Failed to set default dashboard view", err);
    }
  });

  // NOTE: does NOT swallow errors — the edit dialog awaits this and keeps itself
  // open (and the API layer toasts) if the update fails, instead of silently
  // closing with no change.
  const updateViewMeta = useEvent(
    async (
      id: number,
      meta: { name: string; scope: ViewScopeEnum; isDefault: boolean },
    ): Promise<void> => {
      if (!entityId) {
        return;
      }
      const updated = await ViewApi.update(id, {
        entityId,
        name: meta.name,
        scope: meta.scope,
        isDefault: meta.isDefault,
      });
      if (updated) {
        applyUpdatedView(updated);
      } else {
        await reloadViews();
      }
    },
  );

  const resetToDefault = useEvent((): void => {
    setCards(cloneCards(DASHBOARD_DEFAULT_CARDS));
    setActiveViewId(undefined);
  });

  return {
    views: viewList,
    activeViewId,
    defaultView,
    loading,
    generateName,
    saveNewView,
    updateView,
    updateViewMeta,
    applyView,
    clearActiveView,
    deleteView,
    setDefaultView,
    resetToDefault,
  };
};
